import React, { useEffect, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import {
  fetchSetupStatus,
  selectCompanyIsMultiBranch,
  selectIsCompanyMode,
  selectIsOwner,
  selectSetupStatus
} from "../redux/branches.slice.ts"
import { BranchSetupWizard } from "./BranchSetupWizard.tsx"
import { ContinueBranchSetupBanner } from "./ContinueBranchSetupBanner.tsx"

function autoShowKey(gccId: string | null | undefined) {
  return `branch_setup_wizard_auto_shown_${gccId ?? "unknown"}`
}

/**
 * Home's entry point into branch setup — the docstring below always said
 * "for an owner," but nothing actually checked that: this mounts
 * unconditionally from both `WidgetsHome`/`MobileWidgetsHome`, so a Branch
 * Manager's own-branch Home (and the request behind it) fired the same as
 * a GCC Owner's. All four of the following must hold, together, or nothing
 * here renders or fetches at all — never inferred from role, branch count,
 * or "first login":
 *   1. the company is explicitly multi-branch (`selectCompanyIsMultiBranch`)
 *   2. the caller has company-level branch-management authority (`selectIsOwner`)
 *   3. the active workspace is HQ, not an operational branch (`selectIsCompanyMode`)
 *   4. permissions/workspace have actually finished resolving
 *      (`accessibleBranchesValidated`) — `isOwner`/`isCompanyMode` are both
 *      false-by-default before that, so deciding any earlier would either
 *      flash this open for a Branch Manager or hide it from a real owner.
 *
 * Fetches the status every other branch-setup surface already fetches on
 * its own mount (matching this repo's per-component-fetch convention), and
 * auto-opens the wizard once per login session for an owner with
 * incomplete setup — tracked by a `sessionStorage` flag (cleared by a new
 * sign-in) rather than anything persisted server-side, so it nags once per
 * session rather than on every Home visit, while the banner itself always
 * stays available as a one-click way back in. Any already-queued auto-open
 * is cleared the moment the workspace stops being an authorized HQ context
 * (switching into a branch, for example), so it can never surface later.
 */
export function BranchSetupFlow() {
  const dispatch = useAppDispatch()
  const isOwner = useAppSelector(selectIsOwner)
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const accessibleBranchesValidated = useAppSelector(
    (state) => state.branches.accessibleBranchesValidated
  )
  const status = useAppSelector(selectSetupStatus)
  const gccId = useAppSelector((state) => state.branches.gcc?.id)
  const [wizardOpen, setWizardOpen] = useState(false)

  const authorizedForSetup =
    accessibleBranchesValidated && isOwner && isMultiBranch && isCompanyMode

  useEffect(() => {
    if (!authorizedForSetup) return
    dispatch(fetchSetupStatus())
  }, [authorizedForSetup, dispatch])

  useEffect(() => {
    if (!authorizedForSetup) return
    if (!status || status.done || status.target <= 1) return
    const key = autoShowKey(gccId)
    try {
      if (window.sessionStorage.getItem(key)) return
      window.sessionStorage.setItem(key, "1")
    } catch {
      // Storage unavailable (private mode, disabled) — fall back to the
      // banner-only experience rather than auto-opening on every mount.
      return
    }
    setWizardOpen(true)
  }, [authorizedForSetup, status, gccId])

  useEffect(() => {
    if (!authorizedForSetup) setWizardOpen(false)
  }, [authorizedForSetup])

  if (!authorizedForSetup) return null

  return (
    <>
      <ContinueBranchSetupBanner onContinueSetup={() => setWizardOpen(true)} />
      <BranchSetupWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />
    </>
  )
}
